import React, { useState, useEffect } from 'react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';
import { Card, CardContent } from '@shared/components/ui/card';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import { toast } from "@shared/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import {
  FolderKanban,
  Calendar,
  MapPin,
  Users,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Mail,
  ArrowLeft,
  Shield,
  Loader2,
  Download,
  Printer,
  Trash2,
  RefreshCw,
  Search,
} from 'lucide-react';

const ProjectsManagement = () => {
  const { user } = useAuth();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail view state
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTeam, setProjectTeam] = useState([]);
  const [projectDocs, setProjectDocs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [teamInductionStatus, setTeamInductionStatus] = useState({});
  const [leadInductionStatus, setLeadInductionStatus] = useState(null);
  const [updatingDocId, setUpdatingDocId] = useState(null);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [resendingProjectId, setResendingProjectId] = useState(null);
  const [resendingPasses, setResendingPasses] = useState(false);
  const [stats, setStats] = useState({ needsApproval: 0, active: 0, inProgress: 0, total: 0 });

  const handleResendInvite = async (project) => {
    if (!confirm(`Resend the portal invitation email to ${project.project_lead_name} (${project.project_lead_email})?`)) return;
    setResendingProjectId(project.id);
    try {
      const { data, error } = await supabase.functions.invoke('resend-project-invite', {
        body: { project_id: project.id },
      });
      if (error) throw new Error(error.message || 'Failed to resend invite');
      toast({ title: 'Invite Resent', description: `A new invitation email has been sent to ${project.project_lead_email}.` });
    } catch (err) {
      console.error('Error resending invite:', err);
      toast({ title: 'Error', description: err.message || 'Failed to resend invitation', variant: 'destructive' });
    } finally {
      setResendingProjectId(null);
    }
  };

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);


  // Load projects
  useEffect(() => {
    loadProjects(filterStatus);
  }, [filterStatus]);

  const loadStats = async () => {
    try {
      const { data } = await supabase.from('projects').select('status');
      if (data) {
        setStats({
          needsApproval: data.filter(p => p.status === 'submitted_awaiting_approval').length,
          active:        data.filter(p => p.status === 'active').length,
          inProgress:    data.filter(p => ['draft_in_progress', 'draft_pending_supplier'].includes(p.status)).length,
          total:         data.length,
        });
      }
    } catch (err) {
      console.error('Error loading project stats:', err);
    }
  };

  const loadProjects = async (status = filterStatus) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('projects')
        .select(`
          *,
          depots:site_id (name, code),
          created_by:created_by_admin_id (first_name, surname)
        `)
        .order('created_at', { ascending: false });

      if (status !== 'all') {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Sort by status priority: action-needed states first, completed last.
      // Within the same status the server's created_at desc order is preserved.
      const STATUS_PRIORITY = {
        submitted_awaiting_approval: 0, // Needs Approval — always first
        draft_pending_supplier:      1, // Awaiting Supplier
        rejected:                    2, // Rejected
        draft_in_progress:           3, // In Progress
        active:                      4,
        approved:                    5,
        completed:                   6,
      };
      const sorted = (data || []).slice().sort(
        (a, b) => (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9)
      );
      setProjects(sorted);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast({
        title: 'Error',
        description: 'Failed to load projects',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetail = async (project) => {
    setSelectedProject(project);
    setDetailLoading(true);
    try {
      // Load team members
      const { data: teamData } = await supabase
        .from('project_team_members')
        .select('*')
        .eq('project_id', project.id)
        .order('created_at', { ascending: false });

      setProjectTeam(teamData || []);

      // Load documents
      const { data: docsData } = await supabase
        .from('project_documents')
        .select('*')
        .eq('project_id', project.id)
        .order('uploaded_at', { ascending: false });

      setProjectDocs(docsData || []);

      // Check induction completion for each team member
      if (teamData && teamData.length > 0) {
        const statusMap = {};
        for (const member of teamData) {
          const { data: memberSubs } = await supabase
            .from('induction_submissions')
            .select('id, status, brief_id')
            .eq('person_email', member.person_email)
            .eq('depot_id', project.site_id)
            .in('status', ['invited', 'in_progress', 'submitted', 'approved']);

          // Deduplicate by brief_id — keep best status per brief
          const briefMap = {};
          const STATUS_RANK = { approved: 4, submitted: 3, in_progress: 2, invited: 1 };
          (memberSubs || []).forEach(s => {
            const rank = STATUS_RANK[s.status] || 0;
            if (!briefMap[s.brief_id] || rank > (STATUS_RANK[briefMap[s.brief_id].status] || 0)) {
              briefMap[s.brief_id] = s;
            }
          });
          const uniqueBriefs = Object.values(briefMap);
          const total = uniqueBriefs.length;
          const completed = uniqueBriefs.filter(s => s.status === 'approved' || s.status === 'submitted').length;
          statusMap[member.id] = { total, completed, allDone: total > 0 && completed === total };
        }
        setTeamInductionStatus(statusMap);
      }

      // Check project lead induction status
      if (project.project_lead_email) {
        const { data: leadSubs } = await supabase
          .from('induction_submissions')
          .select('id, status, brief_id')
          .eq('person_email', project.project_lead_email)
          .eq('depot_id', project.site_id)
          .in('status', ['invited', 'in_progress', 'submitted', 'approved']);

        // Deduplicate by brief_id — keep best status per brief
        const briefMap = {};
        const STATUS_RANK = { approved: 4, submitted: 3, in_progress: 2, invited: 1 };
        (leadSubs || []).forEach(s => {
          const rank = STATUS_RANK[s.status] || 0;
          if (!briefMap[s.brief_id] || rank > (STATUS_RANK[briefMap[s.brief_id].status] || 0)) {
            briefMap[s.brief_id] = s;
          }
        });
        const uniqueBriefs = Object.values(briefMap);
        const leadTotal = uniqueBriefs.length;
        const leadCompleted = uniqueBriefs.filter(s => s.status === 'approved' || s.status === 'submitted').length;
        setLeadInductionStatus({ total: leadTotal, completed: leadCompleted, allDone: leadTotal > 0 && leadCompleted === leadTotal });
      }
    } catch (error) {
      console.error('Error loading project detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApproveProject = async () => {
    if (!selectedProject) return;
    setApproving(true);
    try {
      // Update project status
      const { error } = await supabase
        .from('projects')
        .update({
          status: 'approved',
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
          approval_notes: 'Approved via admin panel',
        })
        .eq('id', selectedProject.id);

      if (error) throw error;

      // Issue passes for all team members + project lead
      const batchResult = await issueBatchPasses(selectedProject);

      // Notify project lead via email
      try {
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-project-approved`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              project_name: selectedProject.project_name,
              lead_email: selectedProject.project_lead_email,
              lead_name: selectedProject.project_lead_name,
              site_name: selectedProject.depots?.name || 'N/A',
              approved_count: batchResult?.approvedCount ?? 0,
              skipped_count: batchResult?.skippedCount ?? 0,
              skipped_names: batchResult?.skippedNames ?? [],
            }),
          }
        );
      } catch (emailErr) {
        console.warn('Failed to send project approval notification:', emailErr);
      }

      const emailNote = batchResult?.emailFailureCount > 0
        ? ` ${batchResult.emailFailureCount} badge email(s) failed — use "Resend Pass Emails" to retry.`
        : '';
      toast({
        title: 'Project Approved',
        description: `${batchResult?.approvedCount ?? 0} site passes issued.${emailNote}`,
      });

      setSelectedProject(null);
      loadProjects();
      loadStats();
    } catch (error) {
      console.error('Error approving project:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve project',
        variant: 'destructive',
      });
    } finally {
      setApproving(false);
    }
  };

  const handleRejectProject = async () => {
    if (!selectedProject || !rejectionReason.trim()) return;
    setRejecting(true);
    try {
      const { error } = await supabase
        .from('projects')
        .update({
          status: 'rejected',
          rejected_by: user?.id,
          rejected_at: new Date().toISOString(),
          rejection_reason: rejectionReason.trim(),
        })
        .eq('id', selectedProject.id);

      if (error) throw error;

      toast({
        title: 'Project Rejected',
        description: 'The project lead will be notified of the rejection.',
      });

      setShowRejectDialog(false);
      setRejectionReason('');
      setSelectedProject(null);
      loadProjects();
      loadStats();
    } catch (error) {
      console.error('Error rejecting project:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject project',
        variant: 'destructive',
      });
    } finally {
      setRejecting(false);
    }
  };

  const handleResendPassEmails = async () => {
    if (!selectedProject) return;
    if (!confirm(`Resend pass emails to all team members and the project lead for "${selectedProject.project_name}"?`)) return;
    setResendingPasses(true);
    let sent = 0;
    let failed = 0;
    try {
      const { data: freshTeam } = await supabase
        .from('project_team_members')
        .select('*')
        .eq('project_id', selectedProject.id);

      const allPeople = [
        ...(freshTeam || []).map(m => ({ name: m.person_name, email: m.person_email, company: m.person_company || selectedProject.project_lead_company })),
        { name: selectedProject.project_lead_name, email: selectedProject.project_lead_email, company: selectedProject.project_lead_company },
      ];

      const endDate = new Date(selectedProject.estimated_end_date);
      const validUntil = endDate > new Date() ? endDate.toISOString().split('T')[0] : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const { data: { session: authSession } } = await supabase.auth.getSession();

      for (const person of allPeople) {
        const { data: existingPass } = await supabase
          .from('passes')
          .select('id, pass_token, valid_from, valid_until')
          .eq('person_email', person.email.toLowerCase())
          .eq('depot_id', selectedProject.site_id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const passToken = existingPass?.pass_token;
        if (!passToken) {
          console.warn(`No active pass found for ${person.email}, skipping email`);
          failed++;
          continue;
        }

        try {
          const emailRes = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-induction-pass`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authSession?.access_token}`,
              'x-internal-secret': import.meta.env.VITE_INTERNAL_FUNCTION_SECRET || '',
            },
            body: JSON.stringify({
              pass_token: passToken,
              person_name: person.name,
              person_email: person.email,
              person_company: person.company || '',
              person_type: 'contractor',
              valid_from: existingPass.valid_from,
              valid_until: existingPass.valid_until || validUntil,
              reference: `PRJ-${selectedProject.project_name}`,
              qr_data: passToken,
            }),
          });
          const emailData = await emailRes.json();
          if (!emailRes.ok) {
            console.error(`Resend failed for ${person.email}:`, emailData);
            failed++;
          } else {
            sent++;
          }
        } catch (emailErr) {
          console.error(`Resend error for ${person.email}:`, emailErr);
          failed++;
        }
      }

      if (failed === 0) {
        toast({ title: 'Emails Sent', description: `Pass emails resent to all ${sent} recipients.` });
      } else {
        toast({
          title: 'Partial Success',
          description: `${sent} email(s) sent, ${failed} failed. Check the browser console for details.`,
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('Resend pass emails error:', err);
      toast({ title: 'Error', description: err.message || 'Failed to resend pass emails', variant: 'destructive' });
    } finally {
      setResendingPasses(false);
    }
  };

  const issueBatchPasses = async (project) => {
    // NEVER insert directly into `passes` — always go through
    // admin-approve-induction Edge Function → staff_approve_induction RPC
    // so that every pass has an approved_by user, induction_submission_id,
    // and a full audit trail.
    //
    // Each step is a named helper so failures are easy to pinpoint:
    //   loadPeopleMap      → who needs a pass
    //   resolveSubmission  → find their induction record
    //   handleAlreadyApproved → link + email if pass exists; reset if revoked
    //   ensurePtmLinked    → prevent project_id = NULL on new pass
    //   approveViaEdgeFn   → call edge function, link resulting pass

    // ── Helper: load + deduplicate team members + project lead by email ───
    async function loadPeopleMap() {
      const { data: freshTeam, error: teamError } = await supabase
        .from('project_team_members')
        .select('id, person_name, person_email, person_company, induction_submission_id')
        .eq('project_id', project.id);
      if (teamError) console.error('Error loading team for pass issuance:', teamError);

      const map = new Map();
      for (const m of freshTeam || []) {
        const key = m.person_email.toLowerCase().trim();
        if (!map.has(key)) {
          map.set(key, {
            name: m.person_name,
            email: m.person_email,
            company: m.person_company || project.project_lead_company,
            teamMemberId: m.id,
            induction_submission_id: m.induction_submission_id,
          });
        }
      }
      const leadKey = project.project_lead_email.toLowerCase().trim();
      if (!map.has(leadKey)) {
        map.set(leadKey, {
          name: project.project_lead_name,
          email: project.project_lead_email,
          company: project.project_lead_company,
          teamMemberId: null,
          induction_submission_id: null,
        });
      }
      return map;
    }

    // ── Helper: find the best induction submission for a person at this site ─
    async function resolveSubmission(person) {
      // 1. PTM-linked submission (most precise)
      if (person.induction_submission_id) {
        const { data: sub } = await supabase
          .from('induction_submissions')
          .select('id, public_reference, status')
          .eq('id', person.induction_submission_id)
          .maybeSingle();
        if (sub) return sub;
      }
      // 2. Fallback: email + site, best status first
      const { data: sub } = await supabase
        .from('induction_submissions')
        .select('id, public_reference, status')
        .ilike('person_email', person.email)
        .eq('depot_id', project.site_id)
        .in('status', ['submitted', 'approved', 'pending_acknowledgement', 'invited'])
        .order('submitted_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return sub || null;
    }

    // ── Helper: send badge email directly (for already-approved persons) ──
    async function sendPassEmail(person, pass, authToken) {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-induction-pass`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'x-internal-secret': import.meta.env.VITE_INTERNAL_FUNCTION_SECRET || '',
          },
          body: JSON.stringify({
            pass_token:     pass.pass_token,
            person_name:    person.name,
            person_email:   person.email,
            person_company: person.company || '',
            person_type:    'contractor',
            valid_from:     pass.valid_from,
            valid_until:    pass.valid_until,
            reference:      `PRJ-${project.project_name}`,
            qr_data:        pass.pass_token,
            project_name:   project.project_name,
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        console.warn(`Pass email failed for ${person.email}:`, body);
        return false;
      }
      return true;
    }

    // ── Helper: handle person whose submission is already 'approved' ───────
    // • If active pass exists  → link PTM, set project_id, send email
    //   Returns { handled: true, skipped: false, emailFailed: bool }
    // • If no active pass      → reset submission to 'submitted' so the edge
    //   function can re-approve it. Returns { handled: false }
    // • If reset fails         → Returns { handled: true, skipped: true }
    async function handleAlreadyApproved(person, submission, authToken) {
      const { data: existingPass } = await supabase
        .from('passes')
        .select('id, pass_token, valid_from, valid_until')
        .eq('induction_submission_id', submission.id)
        .eq('status', 'active')
        .maybeSingle();

      if (!existingPass) {
        const { error: resetErr } = await supabase
          .from('induction_submissions')
          .update({ status: 'submitted', submitted_at: new Date().toISOString() })
          .eq('id', submission.id);
        if (resetErr) {
          console.warn(`Could not reset submission for ${person.email}:`, resetErr);
          return { handled: true, skipped: true };
        }
        return { handled: false }; // fall through to edge function
      }

      if (person.teamMemberId) {
        await supabase.from('project_team_members')
          .update({ pass_id: existingPass.id, pass_issued_at: new Date().toISOString(), status: 'pass_issued' })
          .eq('id', person.teamMemberId);
      }
      await supabase.from('passes')
        .update({ project_id: project.id })
        .eq('id', existingPass.id)
        .is('project_id', null);

      const emailOk = await sendPassEmail(person, existingPass, authToken).catch(() => false);
      return { handled: true, skipped: false, emailFailed: !emailOk };
    }

    // ── Helper: link PTM to submission so RPC can resolve project_id ──────
    // staff_approve_induction resolves project_id via ptm.induction_submission_id.
    // Without this the pass gets project_id = NULL → badge invisible in portal.
    async function ensurePtmLinked(person, submission) {
      if (person.teamMemberId && !person.induction_submission_id) {
        await supabase
          .from('project_team_members')
          .update({ induction_submission_id: submission.id })
          .eq('id', person.teamMemberId);
      }
    }

    // ── Helper: call admin-approve-induction + link resulting pass ─────────
    // Returns { ok: true, emailFailed: bool } or { ok: false }
    async function approveViaEdgeFn(person, submission, authToken) {
      const approveRes = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-approve-induction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            public_reference: submission.public_reference,
            approve: true,
            notes: `Bulk approved via project sign-off: ${project.project_name}`,
          }),
        }
      );
      const approveData = await approveRes.json();

      if (!approveData.ok) {
        console.error(`Approval failed for ${person.email}:`, approveData);
        toast({
          title: 'Approval Warning',
          description: `Could not issue pass for ${person.name || person.email}: ${approveData?.error?.message || 'Unknown error'}`,
          variant: 'destructive',
        });
        return { ok: false };
      }

      if (approveData.pass_id) {
        if (person.teamMemberId) {
          await supabase.from('project_team_members')
            .update({ pass_id: approveData.pass_id, pass_issued_at: new Date().toISOString(), status: 'pass_issued' })
            .eq('id', person.teamMemberId);
        }
        await supabase.from('passes')
          .update({ project_id: project.id })
          .eq('id', approveData.pass_id)
          .is('project_id', null);
      }

      return { ok: true, emailFailed: !!approveData.email_error };
    }

    // ═══════════════════════════════════════════════════════════════════════
    // Orchestration — runs each helper in sequence per person
    // ═══════════════════════════════════════════════════════════════════════
    const peopleMap = await loadPeopleMap();

    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (!authSession?.access_token) {
      console.error('No auth session for batch approval');
      return;
    }
    const authToken = authSession.access_token;

    let approvedCount = 0;
    let skippedCount = 0;
    const skippedNames = [];
    let emailFailureCount = 0;
    const emailFailureNames = [];

    for (const person of peopleMap.values()) {
      try {
        // Step 1 — find submission
        const submission = await resolveSubmission(person);
        if (!submission) {
          console.warn(`No induction submission found for ${person.email} — skipping`);
          skippedCount++; skippedNames.push(person.name || person.email);
          continue;
        }

        // Step 2 — already-approved path (has pass or needs reset)
        if (submission.status === 'approved') {
          const result = await handleAlreadyApproved(person, submission, authToken);
          if (result.skipped) { skippedCount++; skippedNames.push(person.name || person.email); continue; }
          if (result.handled) {
            if (result.emailFailed) { emailFailureCount++; emailFailureNames.push(person.name || person.email); }
            approvedCount++;
            continue;
          }
          // handled=false → submission was reset to 'submitted', fall through
        }

        // Step 3 — guard: must be in an approvable state
        if (!['submitted', 'pending_acknowledgement', 'invited'].includes(submission.status)) {
          console.warn(`Submission for ${person.email} is "${submission.status}" — cannot approve`);
          skippedCount++; skippedNames.push(person.name || person.email);
          continue;
        }

        // Step 4 — link PTM → submission so RPC resolves project_id
        await ensurePtmLinked(person, submission);

        // Step 5 — approve via edge function, link resulting pass
        const result = await approveViaEdgeFn(person, submission, authToken);
        if (!result.ok) { skippedCount++; skippedNames.push(person.name || person.email); continue; }
        if (result.emailFailed) { emailFailureCount++; emailFailureNames.push(person.name || person.email); }
        approvedCount++;

      } catch (err) {
        console.error(`Error processing ${person.email}:`, err);
        skippedCount++; skippedNames.push(person.name || person.email);
      }
    }

    if (skippedCount > 0) {
      toast({
        title: 'Some team members skipped',
        description: `${approvedCount} approved. ${skippedCount} skipped (induction not yet submitted): ${skippedNames.join(', ')}`,
        variant: 'default',
      });
    }
    if (emailFailureCount > 0) {
      toast({
        title: 'Pass emails not sent',
        description: `${emailFailureCount} badge email(s) could not be delivered: ${emailFailureNames.join(', ')}. Use "Resend Pass Emails" to retry.`,
        variant: 'destructive',
      });
    }

    return { approvedCount, skippedCount, skippedNames, emailFailureCount, emailFailureNames };
  };

  // Extract storage path from the stored file_url
  const getStoragePath = (fileUrl) => {
    if (!fileUrl) return null;
    const marker = 'project-documents/';
    const idx = fileUrl.indexOf(marker);
    if (idx === -1) return null;
    return fileUrl.substring(idx + marker.length);
  };

  const handleDocumentStatusUpdate = async (doc, newStatus) => {
    setUpdatingDocId(doc.id);
    try {
      const { error } = await supabase
        .from('project_documents')
        .update({
          status: newStatus,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user?.id,
        })
        .eq('id', doc.id);

      if (error) throw error;

      // Update local state
      setProjectDocs(prev => prev.map(d =>
        d.id === doc.id ? { ...d, status: newStatus, reviewed_at: new Date().toISOString() } : d
      ));

      toast({
        title: newStatus === 'accepted' ? 'Document Accepted' : 'Document Rejected',
        description: `${doc.file_name} has been ${newStatus}.`,
      });
    } catch (error) {
      console.error('Error updating document status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update document status.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingDocId(null);
    }
  };

  const handleDocumentDownload = async (doc) => {
    setDownloadingDocId(doc.id);
    try {
      const storagePath = getStoragePath(doc.file_url);
      if (!storagePath) {
        throw new Error('Could not determine file path');
      }

      // Create signed URL for private bucket (valid for 60 seconds)
      const { data, error } = await supabase.storage
        .from('project-documents')
        .createSignedUrl(storagePath, 60);

      if (error) throw error;

      // Open in new tab for viewing / trigger download
      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: 'Download Error',
        description: 'Failed to generate download link. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleDocumentPrint = async (doc) => {
    try {
      const storagePath = getStoragePath(doc.file_url);
      if (!storagePath) {
        throw new Error('Could not determine file path');
      }

      const { data, error } = await supabase.storage
        .from('project-documents')
        .createSignedUrl(storagePath, 60);

      if (error) throw error;

      // Open in a print-friendly window
      const printWindow = window.open(data.signedUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch (error) {
      console.error('Error printing document:', error);
      toast({
        title: 'Print Error',
        description: 'Failed to open document for printing.',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft_pending_supplier: {
        label: 'Awaiting Supplier',
        icon: Clock,
        className: 'bg-amber-100 text-amber-800 border-amber-200',
      },
      draft_in_progress: {
        label: 'In Progress',
        icon: AlertCircle,
        className: 'bg-blue-100 text-blue-800 border-blue-200',
      },
      submitted_awaiting_approval: {
        label: 'Needs Approval',
        icon: AlertCircle,
        className: 'bg-orange-100 text-orange-800 border-orange-200',
      },
      approved: {
        label: 'Approved',
        icon: CheckCircle,
        className: 'bg-green-100 text-green-800 border-green-200',
      },
      rejected: {
        label: 'Rejected',
        icon: XCircle,
        className: 'bg-red-100 text-red-800 border-red-200',
      },
      active: {
        label: 'Active',
        icon: CheckCircle,
        className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      },
      completed: {
        label: 'Completed',
        icon: CheckCircle,
        className: 'bg-gray-100 text-gray-800 border-gray-200',
      },
    };

    const config = statusConfig[status] || statusConfig.draft_pending_supplier;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium border ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="p-6 space-y-6">
      {selectedProject ? (
        /* ========== PROJECT DETAIL VIEW ========== */
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{selectedProject.project_name}</h1>
                <p className="text-sm text-muted-foreground">
                  {selectedProject.depots?.name} &bull; Lead: {selectedProject.project_lead_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(selectedProject.status)}
            </div>
          </div>

          {detailLoading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-2">Loading project details...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Project Info */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> Project Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Project Lead:</span> <span className="font-medium">{selectedProject.project_lead_name}</span></div>
                  <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{selectedProject.project_lead_email}</span></div>
                  <div><span className="text-muted-foreground">Company:</span> <span className="font-medium">{selectedProject.project_lead_company || 'N/A'}</span></div>
                  <div><span className="text-muted-foreground">Duration:</span> <span className="font-medium">{formatDate(selectedProject.estimated_start_date)} - {formatDate(selectedProject.estimated_end_date)}</span></div>
                  {selectedProject.project_summary && (
                    <div className="col-span-2"><span className="text-muted-foreground">Summary:</span> <span className="font-medium">{selectedProject.project_summary}</span></div>
                  )}
                  {selectedProject.rejection_reason && (
                    <div className="col-span-2 text-red-600"><span className="text-muted-foreground">Rejection Reason:</span> <span className="font-medium">{selectedProject.rejection_reason}</span></div>
                  )}
                </div>
              </div>

              {/* Documents */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> RAMS Documents ({projectDocs.length})
                </h3>
                {projectDocs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No documents uploaded.</p>
                ) : (
                  <div className="space-y-3">
                    {projectDocs.map(doc => {
                      const isUpdating = updatingDocId === doc.id;
                      const isDownloading = downloadingDocId === doc.id;
                      return (
                        <div key={doc.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <p className="text-sm font-medium truncate">{doc.file_name}</p>
                                {doc.version > 1 && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">v{doc.version}</span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground ml-6">
                                {doc.document_type?.toUpperCase()} &bull; {formatDate(doc.uploaded_at)}
                                {doc.uploaded_by_name && <> &bull; Uploaded by {doc.uploaded_by_name}</>}
                              </p>
                              {doc.version_notes && (
                                <p className="text-xs text-muted-foreground ml-6 mt-1 italic">{doc.version_notes}</p>
                              )}
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                              doc.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-amber-100 text-amber-800'
                            }`}>
                              {doc.status === 'accepted' ? 'Accepted' : doc.status === 'rejected' ? 'Rejected' : 'Pending Review'}
                            </span>
                          </div>

                          {/* Document Action Buttons */}
                          <div className="flex items-center gap-2 mt-3 ml-6">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDocumentDownload(doc)}
                              disabled={isDownloading}
                              className="h-8"
                            >
                              {isDownloading ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                              ) : (
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                              )}
                              View / Download
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDocumentPrint(doc)}
                              className="h-8"
                            >
                              <Printer className="h-3.5 w-3.5 mr-1.5" />
                              Print
                            </Button>

                            {/* Accept / Reject buttons */}
                            {doc.status !== 'accepted' && (
                              <Button
                                size="sm"
                                onClick={() => handleDocumentStatusUpdate(doc, 'accepted')}
                                disabled={isUpdating}
                                className="h-8 bg-green-600 hover:bg-green-700 text-white"
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                ) : (
                                  <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                                )}
                                Accept
                              </Button>
                            )}
                            {doc.status !== 'rejected' && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDocumentStatusUpdate(doc, 'rejected')}
                                disabled={isUpdating}
                                className="h-8"
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                ) : (
                                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                                )}
                                Reject
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Project Personnel */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" /> Project Personnel ({1 + projectTeam.length})
                </h3>

                {/* Project Lead */}
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Project Lead</p>
                <div className="flex items-center justify-between p-3 border-2 rounded-lg mb-4">
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{selectedProject.project_lead_name}</p>
                    <p className="text-xs text-muted-foreground">{selectedProject.project_lead_email}</p>
                    {selectedProject.project_lead_company && (
                      <p className="text-xs text-muted-foreground">{selectedProject.project_lead_company}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedProject.project_lead_phone && (
                      <span className="text-xs text-muted-foreground">{selectedProject.project_lead_phone}</span>
                    )}
                    {leadInductionStatus && leadInductionStatus.total > 0 && (
                      <span className={`text-xs font-medium ${leadInductionStatus.allDone ? 'text-green-600' : 'text-amber-600'}`}>
                        {leadInductionStatus.completed}/{leadInductionStatus.total} briefs
                      </span>
                    )}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
                      Lead
                    </span>
                    {leadInductionStatus && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        leadInductionStatus.allDone ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {leadInductionStatus.allDone ? 'Inductions Complete' : 'In Progress'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Team Members */}
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Team Members</p>
                {projectTeam.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No additional team members added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {projectTeam.map(member => {
                      const inductionInfo = teamInductionStatus[member.id];
                      return (
                        <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{member.person_name}</p>
                            <p className="text-xs text-muted-foreground">{member.person_email}</p>
                            {member.person_company && <p className="text-xs text-muted-foreground">{member.person_company}</p>}
                          </div>
                          <div className="flex items-center gap-3">
                            {inductionInfo && (
                              <span className={`text-xs ${inductionInfo.allDone ? 'text-green-600' : 'text-amber-600'}`}>
                                {inductionInfo.completed}/{inductionInfo.total} briefs
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              member.pass_id ? 'bg-green-100 text-green-800' :
                              inductionInfo?.allDone ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {member.pass_id ? 'Pass Issued' : inductionInfo?.allDone ? 'Inductions Complete' : 'In Progress'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Resend Pass Emails (approved / active projects) */}
              {(selectedProject.status === 'approved' || selectedProject.status === 'active') && (
                <div className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">Pass Emails</p>
                    <p className="text-xs text-muted-foreground">Resend site pass emails to all project personnel</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={resendingPasses}
                    onClick={handleResendPassEmails}
                  >
                    {resendingPasses ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                    ) : (
                      <><Mail className="h-4 w-4 mr-2" />Resend Pass Emails</>
                    )}
                  </Button>
                </div>
              )}

              {/* Approval Actions */}
              {selectedProject.status === 'submitted_awaiting_approval' && (
                <div className="bg-card border-2 border-orange-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-orange-600" /> Approval Required
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    This project has been submitted for review. Approving will issue site passes to all {projectTeam.length} team members and the project lead.
                  </p>
                  <div className="flex items-center gap-3">
                    <Button onClick={handleApproveProject} disabled={approving} className="bg-green-600 hover:bg-green-700">
                      {approving ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Approving &amp; Issuing Passes...</>
                      ) : (
                        <><CheckCircle className="h-4 w-4 mr-2" />Approve &amp; Issue Passes</>
                      )}
                    </Button>
                    <Button variant="destructive" onClick={() => setShowRejectDialog(true)} disabled={approving}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reject Dialog */}
          <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reject Project</DialogTitle>
                <DialogDescription>
                  Please provide a reason for rejecting this project. The project lead will be notified.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Textarea
                  placeholder="Enter rejection reason..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowRejectDialog(false)} disabled={rejecting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleRejectProject} disabled={rejecting || !rejectionReason.trim()}>
                  {rejecting ? 'Rejecting...' : 'Reject Project'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      ) : (
      /* ========== PROJECT LIST VIEW ========== */
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Site Projects</h1>
          <p className="text-muted-foreground mt-1">
            Manage projects, RAMS, and contractor teams
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Projects',  value: stats.total,        color: 'text-foreground',  filter: 'all',                          icon: FolderKanban },
          { label: 'Needs Approval',  value: stats.needsApproval, color: 'text-orange-600',  filter: 'submitted_awaiting_approval',  icon: AlertCircle },
          { label: 'In Progress',     value: stats.inProgress,   color: 'text-blue-600',    filter: 'draft_in_progress',            icon: Clock },
          { label: 'Active',          value: stats.active,       color: 'text-green-600',   filter: 'active',                       icon: CheckCircle },
        ].map(({ label, value, color, filter, icon: Icon }) => (
          <button
            key={label}
            onClick={() => setFilterStatus(f => f === filter && filter !== 'all' ? 'all' : filter)}
            className={`text-left px-4 py-4 rounded-md border transition-colors bg-background hover:bg-muted/40 ${
              filterStatus === filter ? 'border-foreground/30 bg-muted/40' : 'border-border'
            }`}
          >
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${color}`}>{value}</p>
          </button>
        ))}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by project name, lead, company, or site..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        {filterStatus !== 'all' && (
          <Button variant="outline" onClick={() => setFilterStatus('all')}>
            Clear Filter
          </Button>
        )}
        <Button variant="outline" onClick={loadProjects}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Projects List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading projects...</p>
            </div>
          </CardContent>
        </Card>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              {filterStatus === 'all' ? (
                <>
                  <h3 className="text-lg font-semibold">No projects yet</h3>
                  <p className="text-muted-foreground mt-2">
                    Create your first project to get started.
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Use the invite modal to create a new project.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold">No matching projects</h3>
                  <p className="text-muted-foreground mt-2">
                    No projects found with this status. Try a different filter.
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        (() => {
          // Needs Approval always first, then by newest start date
          const STATUS_PRIORITY = {
            submitted_awaiting_approval: 0,
            draft_pending_supplier:      1,
            rejected:                    2,
            draft_in_progress:           3,
            active:                      4,
            approved:                    5,
            completed:                   6,
          };
          const filtered = searchTerm
            ? projects.filter(p => {
                const term = searchTerm.toLowerCase();
                return (
                  p.project_name?.toLowerCase().includes(term) ||
                  p.project_lead_name?.toLowerCase().includes(term) ||
                  p.project_lead_company?.toLowerCase().includes(term) ||
                  p.depots?.name?.toLowerCase().includes(term)
                );
              })
            : projects;

          const sorted = [...filtered].sort((a, b) => {
            const pa = STATUS_PRIORITY[a.status] ?? 9;
            const pb = STATUS_PRIORITY[b.status] ?? 9;
            if (pa !== pb) return pa - pb;
            return new Date(b.estimated_start_date || 0) - new Date(a.estimated_start_date || 0);
          });

          return (
            <div className="rounded-md border border-border overflow-hidden bg-background">
              {/* Column headers */}
              <div className="hidden md:grid grid-cols-[200px_130px_110px_130px_130px_160px_130px_170px] gap-0 px-5 py-2.5 border-b bg-muted/40 text-[10.5px] font-semibold uppercase tracking-widest text-muted-foreground/80">
                <span>Project</span>
                <span>Person</span>
                <span>Role</span>
                <span>Company</span>
                <span>Site</span>
                <span>Duration</span>
                <span>Status</span>
                <span className="text-right">Actions</span>
              </div>

              <div className="divide-y divide-border">
                {sorted.map((project) => (
                  <React.Fragment key={project.id}>
                    {/* ── Desktop row ── */}
                    <div
                      className="hidden md:grid grid-cols-[200px_130px_110px_130px_130px_160px_130px_170px] gap-0 px-5 py-3.5 items-center hover:bg-muted/20 transition-colors"
                    >
                      {/* Project name + summary */}
                      <div className="pr-4 min-w-0">
                        <span className="font-medium text-sm block truncate">{project.project_name}</span>
                        <span className="text-xs text-muted-foreground block mt-0.5 truncate">
                          {project.project_summary || <span className="italic opacity-50">No description</span>}
                        </span>
                      </div>

                      {/* Person */}
                      <div className="pr-3 min-w-0">
                        <span className="text-sm font-medium text-foreground truncate block">{project.project_lead_name || '—'}</span>
                      </div>

                      {/* Role */}
                      <div className="pr-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border border-blue-300 text-blue-700 bg-blue-50 whitespace-nowrap">
                          Project Lead
                        </span>
                      </div>

                      {/* Company */}
                      <div className="pr-3 min-w-0">
                        <span className="text-sm text-muted-foreground truncate block">{project.project_lead_company || '—'}</span>
                      </div>

                      {/* Site */}
                      <div className="pr-3">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{project.depots?.name || '—'}</span>
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="pr-3">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                          <Calendar className="h-3 w-3 shrink-0" />
                          <span>{formatDate(project.estimated_start_date)}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground/70 mt-0.5 pl-4 whitespace-nowrap">
                          → {formatDate(project.estimated_end_date)}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="flex items-center">
                        {getStatusBadge(project.status)}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1.5 justify-end">
                        {project.status === 'draft_pending_supplier' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 text-amber-700 border-amber-300 hover:bg-amber-50 shrink-0"
                            disabled={resendingProjectId === project.id}
                            onClick={() => handleResendInvite(project)}
                            title="Resend invitation"
                          >
                            {resendingProjectId === project.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <RefreshCw className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                        {project.status === 'submitted_awaiting_approval' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 border-orange-400 text-orange-600 hover:bg-orange-50 font-medium shrink-0"
                            onClick={() => loadProjectDetail(project)}
                            title="Review project"
                          >
                            <Shield className="h-3.5 w-3.5 mr-1.5" />
                            Review
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 shrink-0"
                          onClick={() => loadProjectDetail(project)}
                          title="View details"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                          disabled={deletingProjectId === project.id}
                          title="Delete project"
                          onClick={async () => {
                            if (!confirm(`Delete project "${project.project_name}"? This will also remove all team members and documents. This cannot be undone.`)) return;
                            setDeletingProjectId(project.id);
                            try {
                              const { error } = await supabase.from('projects').delete().eq('id', project.id);
                              if (error) throw error;
                              setProjects(prev => prev.filter(p => p.id !== project.id));
                              toast({ title: 'Project deleted', description: `"${project.project_name}" has been deleted.` });
                            } catch (err) {
                              console.error('Error deleting project:', err);
                              toast({ title: 'Error', description: 'Failed to delete project', variant: 'destructive' });
                            } finally {
                              setDeletingProjectId(null);
                            }
                          }}
                        >
                          {deletingProjectId === project.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>

                    {/* ── Mobile card ── */}
                    <div className="md:hidden px-4 py-3.5 space-y-2 hover:bg-muted/20 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-semibold text-sm block truncate">{project.project_name}</span>
                          {project.project_summary && (
                            <span className="text-xs text-muted-foreground block mt-0.5 truncate">{project.project_summary}</span>
                          )}
                        </div>
                        <div className="shrink-0">{getStatusBadge(project.status)}</div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{project.project_lead_name || '—'}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border border-blue-300 text-blue-700 bg-blue-50">Project Lead</span>
                        {project.project_lead_company && <span>{project.project_lead_company}</span>}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          {project.depots?.name && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span>{project.depots.name}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(project.estimated_start_date)} → {formatDate(project.estimated_end_date)}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          {project.status === 'draft_pending_supplier' && (
                            <Button variant="outline" size="sm"
                              className="h-8 w-8 p-0 text-amber-700 border-amber-300"
                              disabled={resendingProjectId === project.id}
                              onClick={() => handleResendInvite(project)} title="Resend invitation">
                              {resendingProjectId === project.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                          {project.status === 'submitted_awaiting_approval' && (
                            <Button size="sm" variant="outline"
                              className="h-8 px-3 border-orange-400 text-orange-600 hover:bg-orange-50 font-medium"
                              onClick={() => loadProjectDetail(project)}>
                              <Shield className="h-3.5 w-3.5 mr-1.5" />Review
                            </Button>
                          )}
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                            onClick={() => loadProjectDetail(project)} title="View details">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            disabled={deletingProjectId === project.id}
                            onClick={async () => {
                              if (!confirm(`Delete project "${project.project_name}"? This will also remove all team members and documents. This cannot be undone.`)) return;
                              setDeletingProjectId(project.id);
                              try {
                                const { error } = await supabase.from('projects').delete().eq('id', project.id);
                                if (error) throw error;
                                setProjects(prev => prev.filter(p => p.id !== project.id));
                                toast({ title: 'Project deleted', description: `"${project.project_name}" has been deleted.` });
                              } catch (err) {
                                toast({ title: 'Error', description: 'Failed to delete project', variant: 'destructive' });
                              } finally {
                                setDeletingProjectId(null);
                              }
                            }}>
                            {deletingProjectId === project.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })()
      )}

      </>
      )}
    </div>
  );
};

export default ProjectsManagement;
